/**
 * Compatibility layer for Physical Count UI: maps OpenAPI-generated client to session helpers
 * and stable domain types (avoids losing hand-written exports on `npm run generate:api`).
 *
 * Source of truth: this file lives under scripts/openapi-extras/ and is copied into src/api/
 * after each `openapi-typescript-codegen` run (see scripts/fix-openapi.js).
 */
import { OpenAPI } from './core/OpenAPI';
import { request as __request } from './core/request';
import { PhysicalCountService as GeneratedPhysicalCountService } from './services/PhysicalCountService';
import type { PhysicalCountSessionResponseDto } from './models/PhysicalCountSessionResponseDto';
import type { PhysicalCountDiscrepancyDto } from './models/PhysicalCountDiscrepancyDto';
import type { PhysicalCountDuplicateDto } from './models/PhysicalCountDuplicateDto';
import type { WriteOffMissingDto } from './models/WriteOffMissingDto';
import type { WriteOffMissingResponseDto } from './models/WriteOffMissingResponseDto';
import type {
  PhysicalCountSession,
  PhysicalCountDiscrepancy,
  PhysicalCountDuplicate,
  PhysicalCountSessionSummary,
  PhysicalCountDetailGroupKey,
  PhysicalCountItemsPage,
  PhysicalCountParsePreviewResponse,
} from '../types/physicalCount.types';

function mapDiscrepancy(d: PhysicalCountDiscrepancyDto): PhysicalCountDiscrepancy {
  const typeMap: Record<string, PhysicalCountDiscrepancy['type']> = {
    missing: 'missing',
    excess: 'surplus',
    scanned_not_available: 'scanned_not_available',
  };
  return {
    id: d.id,
    productCode: d.productCode,
    productUniqueCode: d.productUniqueCode,
    productName: d.productName,
    expectedQty: d.expectedQty,
    actualQty: d.actualQty,
    type: typeMap[d.discrepancyType] ?? 'missing',
    availabilityStatus: d.availabilityStatus,
    writtenOffAt: d.writtenOffAt,
    writtenOffBy: d.writtenOffBy,
  };
}

function mapDuplicate(d: PhysicalCountDuplicateDto): PhysicalCountDuplicate {
  return {
    scannedCode: d.scannedCode,
    timesScanned: d.timesScanned,
  };
}

/** Maps API session summary DTO to UI type. */
export function mapSessionSummaryFromApi(raw: unknown): PhysicalCountSessionSummary {
  const s = raw as {
    id: number;
    code: string;
    status: 'open' | 'finalized';
    locationId: number;
    locationName: string;
    sessionDate: string;
    comparedAt?: string;
    expectedCount: number;
    actualCount: number;
    successCount: number;
    missingCount: number;
    pendingMissingCount?: number;
    excessCount: number;
    duplicateCount: number;
    scannedNotAvailableCount: number;
    createdBy?: string;
    createdAt: string;
  };
  return {
    id: s.id,
    code: s.code,
    status: s.status,
    locationId: s.locationId,
    locationName: s.locationName,
    sessionDate: s.sessionDate,
    comparedAt: s.comparedAt,
    expectedCount: s.expectedCount,
    actualCount: s.actualCount,
    okCount: s.successCount,
    missingCount: s.missingCount,
    pendingMissingCount: s.pendingMissingCount,
    surplusCount: s.excessCount,
    duplicateCount: s.duplicateCount,
    scannedNotAvailableCount: s.scannedNotAvailableCount,
    createdBy: s.createdBy,
    createdAt: s.createdAt,
  };
}

export function mapItemsPageFromApi(raw: unknown): PhysicalCountItemsPage {
  const r = raw as PhysicalCountItemsPage;
  return {
    group: r.group,
    data: r.data ?? [],
    meta: r.meta,
  };
}

/** Maps API session DTO to UI `PhysicalCountSession`. */
export function mapSessionFromApi(raw: unknown): PhysicalCountSession {
  const s = raw as PhysicalCountSessionResponseDto;
  const status: PhysicalCountSession['status'] =
    s.status === 'open' || s.status === 'finalized' ? s.status : 'open';
  return {
    id: s.id,
    code: s.code,
    status,
    locationId: s.locationId,
    locationName: s.locationName,
    sessionDate: s.sessionDate,
    comparedAt: s.comparedAt,
    expectedCount: s.expectedCount,
    actualCount: s.actualCount,
    okCount: s.successCount,
    missingCount: s.missingCount,
    surplusCount: s.excessCount,
    duplicateCount: s.duplicateCount,
    scannedNotAvailableCount: s.scannedNotAvailableCount,
    createdBy: s.createdBy,
    createdAt: s.createdAt,
    discrepancies: (s.discrepancies ?? []).map(mapDiscrepancy),
    duplicates: (s.duplicates ?? []).map(mapDuplicate),
  };
}

export const PhysicalCountService = {
  sessionsFindAll: (params: { code?: string; locationId?: number; page: number; limit: number }) =>
    GeneratedPhysicalCountService.physicalCountControllerFindAll(
      params.code,
      params.locationId,
      params.page,
      params.limit,
    ),

  sessionsFindOne: (id: number) => GeneratedPhysicalCountService.physicalCountControllerFindOne(id),

  sessionsFindSummary: (id: number): Promise<PhysicalCountSessionSummary> =>
    __request(OpenAPI, {
      method: 'GET',
      url: '/physical-count/sessions/{id}/summary',
      path: { id },
    }).then((res) => mapSessionSummaryFromApi(res)),

  sessionsFindItems: (
    id: number,
    params: { group: PhysicalCountDetailGroupKey; page?: number; limit?: number },
  ): Promise<PhysicalCountItemsPage> =>
    __request(OpenAPI, {
      method: 'GET',
      url: '/physical-count/sessions/{id}/items',
      path: { id },
      query: {
        group: params.group,
        page: params.page ?? 1,
        limit: params.limit ?? 10,
      },
    }).then((res) => mapItemsPageFromApi(res)),

  parsePreview: (
    locationId: number,
    file: File,
    parseMode: 'single_row' | 'single_column' | 'auto' = 'single_column',
    normalization: 'preserve_case' | 'uppercase' = 'preserve_case',
  ): Promise<PhysicalCountParsePreviewResponse> =>
    __request(OpenAPI, {
      method: 'POST',
      url: '/physical-count/parse-preview',
      formData: { file, locationId, parseMode, normalization },
      mediaType: 'multipart/form-data',
    }) as Promise<PhysicalCountParsePreviewResponse>,

  sessionsImportAndFinalize: (
    locationId: number,
    sessionDate: string,
    file: File,
    parseMode: 'single_row' | 'single_column' | 'auto' = 'single_column',
    normalization: 'preserve_case' | 'uppercase' = 'preserve_case',
    notes?: string,
  ): Promise<PhysicalCountSessionSummary> =>
    __request(OpenAPI, {
      method: 'POST',
      url: '/physical-count/sessions/import-and-finalize',
      formData: {
        file,
        locationId,
        sessionDate,
        parseMode,
        normalization,
        ...(notes != null ? { notes } : {}),
      },
      mediaType: 'multipart/form-data',
    }).then((res) => mapSessionSummaryFromApi(res)),

  sessionsCreate: (locationId: number, sessionDate: string, notes?: string) =>
    GeneratedPhysicalCountService.physicalCountControllerCreate({
      locationId,
      sessionDate,
      notes,
    }),

  sessionsUploadScansFile: (
    id: number,
    file: File,
    parseMode: 'single_row' | 'single_column' | 'auto',
    normalization: 'preserve_case' | 'uppercase',
  ) =>
    GeneratedPhysicalCountService.physicalCountControllerUploadScansFile(id, {
      file,
      parseMode,
      normalization,
    }),

  sessionsFinalizeSession: (id: number) =>
    GeneratedPhysicalCountService.physicalCountControllerFinalizeSession(id),

  sessionsWriteOffMissing: (
    id: number,
    body: { discrepancyId?: number; allMissing?: boolean },
  ): Promise<WriteOffMissingResponseDto> =>
    GeneratedPhysicalCountService.physicalCountControllerWriteOffMissing(
      id,
      body as WriteOffMissingDto,
    ),

  exportExpectedInventory: (locationId: number) =>
    GeneratedPhysicalCountService.physicalCountControllerExport(locationId),
};
